'use client';

import _defineProperty from "/codebuild/output/src3665/src/s3/01/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import "core-js/modules/es.date.to-string.js";
import { keys } from '../../utils/keys/keys.mjs';
import { rem } from '../../utils/units-converters/rem.mjs';
import 'react';
import 'react/jsx-runtime';
import '@mantine/hooks';
import { getPrimaryShade } from '../color-functions/get-primary-shade/get-primary-shade.mjs';
import { getPrimaryContrastColor } from '../color-functions/get-contrast-color/get-contrast-color.mjs';
import { getCSSColorVariables } from './get-css-color-variables.mjs';
import { isVirtualColor } from './virtual-color/virtual-color.mjs';
function assignSizeVariables(variables, sizes, name) {
  keys(sizes).forEach(function (size) {
    return Object.assign(variables, _defineProperty({}, "--mantine-".concat(name, "-").concat(size), sizes[size]));
  });
}
var defaultCssVariablesResolver = function defaultCssVariablesResolver(theme) {
  var lightPrimaryShade = getPrimaryShade(theme, "light");
  var defaultRadius = theme.defaultRadius in theme.radius ? theme.radius[theme.defaultRadius] : rem(theme.defaultRadius);
  var result = {
    variables: {
      "--mantine-scale": theme.scale.toString(),
      "--mantine-cursor-type": theme.cursorType,
      "--mantine-color-scheme": "light dark",
      "--mantine-webkit-font-smoothing": theme.fontSmoothing ? "antialiased" : "unset",
      "--mantine-moz-font-smoothing": theme.fontSmoothing ? "grayscale" : "unset",
      "--mantine-color-white": theme.white,
      "--mantine-color-black": theme.black,
      "--mantine-line-height": theme.lineHeights.md,
      "--mantine-font-family": theme.fontFamily,
      "--mantine-font-family-monospace": theme.fontFamilyMonospace,
      "--mantine-font-family-headings": theme.headings.fontFamily,
      "--mantine-heading-font-weight": theme.headings.fontWeight,
      "--mantine-heading-text-wrap": theme.headings.textWrap,
      "--mantine-radius-default": defaultRadius,
      // Primary colors
      "--mantine-primary-color-filled": "var(--mantine-color-".concat(theme.primaryColor, "-filled)"),
      "--mantine-primary-color-filled-hover": "var(--mantine-color-".concat(theme.primaryColor, "-filled-hover)"),
      "--mantine-primary-color-light": "var(--mantine-color-".concat(theme.primaryColor, "-light)"),
      "--mantine-primary-color-light-hover": "var(--mantine-color-".concat(theme.primaryColor, "-light-hover)"),
      "--mantine-primary-color-light-color": "var(--mantine-color-".concat(theme.primaryColor, "-light-color)")
    },
    light: {
      "--mantine-primary-color-contrast": getPrimaryContrastColor(theme, "light"),
      "--mantine-color-bright": "var(--mantine-color-black)",
      "--mantine-color-text": theme.black,
      "--mantine-color-body": theme.white,
      "--mantine-color-error": "var(--mantine-color-red-6)",
      "--mantine-color-placeholder": "var(--mantine-color-gray-5)",
      "--mantine-color-anchor": "var(--mantine-color-".concat(theme.primaryColor, "-").concat(lightPrimaryShade, ")"),
      "--mantine-color-default": "var(--mantine-color-white)",
      "--mantine-color-default-hover": "var(--mantine-color-gray-0)",
      "--mantine-color-default-color": "var(--mantine-color-black)",
      "--mantine-color-default-border": "var(--mantine-color-gray-4)",
      "--mantine-color-dimmed": "var(--mantine-color-gray-6)"
    },
    dark: {
      "--mantine-primary-color-contrast": getPrimaryContrastColor(theme, "dark"),
      "--mantine-color-bright": "var(--mantine-color-white)",
      "--mantine-color-text": "var(--mantine-color-dark-0)",
      "--mantine-color-body": "var(--mantine-color-dark-7)",
      "--mantine-color-error": "var(--mantine-color-red-8)",
      "--mantine-color-placeholder": "var(--mantine-color-dark-3)",
      "--mantine-color-anchor": "var(--mantine-color-".concat(theme.primaryColor, "-4)"),
      "--mantine-color-default": "var(--mantine-color-dark-6)",
      "--mantine-color-default-hover": "var(--mantine-color-dark-5)",
      "--mantine-color-default-color": "var(--mantine-color-white)",
      "--mantine-color-default-border": "var(--mantine-color-dark-4)",
      "--mantine-color-dimmed": "var(--mantine-color-dark-2)"
    }
  };
  assignSizeVariables(result.variables, theme.breakpoints, "breakpoint");
  assignSizeVariables(result.variables, theme.spacing, "spacing");
  assignSizeVariables(result.variables, theme.fontSizes, "font-size");
  assignSizeVariables(result.variables, theme.lineHeights, "line-height");
  assignSizeVariables(result.variables, theme.shadows, "shadow");
  assignSizeVariables(result.variables, theme.radius, "radius");
  theme.colors[theme.primaryColor].forEach(function (_, index) {
    result.variables["--mantine-primary-color-".concat(index)] = "var(--mantine-color-".concat(theme.primaryColor, "-").concat(index, ")");
  });
  keys(theme.colors).forEach(function (color) {
    var value = theme.colors[color];
    if (isVirtualColor(value)) {
      Object.assign(result.light, getCSSColorVariables({
        theme: theme,
        name: value.name,
        color: value.light,
        colorScheme: "light",
        withColorValues: true
      }));
      Object.assign(result.dark, getCSSColorVariables({
        theme: theme,
        name: value.name,
        color: value.dark,
        colorScheme: "dark",
        withColorValues: true
      }));
      return;
    }
    value.forEach(function (shade, index) {
      result.variables["--mantine-color-".concat(color, "-").concat(index)] = shade;
    });
    Object.assign(result.light, getCSSColorVariables({
      theme: theme,
      color: color,
      colorScheme: "light",
      withColorValues: false
    }));
    Object.assign(result.dark, getCSSColorVariables({
      theme: theme,
      color: color,
      colorScheme: "dark",
      withColorValues: false
    }));
  });
  var headings = theme.headings.sizes;
  keys(headings).forEach(function (heading) {
    result.variables["--mantine-".concat(heading, "-font-size")] = headings[heading].fontSize;
    result.variables["--mantine-".concat(heading, "-line-height")] = headings[heading].lineHeight;
    result.variables["--mantine-".concat(heading, "-font-weight")] = headings[heading].fontWeight || theme.headings.fontWeight;
  });
  return result;
};
export { defaultCssVariablesResolver };